/* eslint-disable no-bitwise */

import { EMPTY_IMAGE_PLACEHOLDER, BASE_64_SVG_IMAGE_PREFIX, CROP_MARKER_COLOR } from './constants';
import { getExportWidthPx } from './get-export-width.utils';

let GOOGLE_FONTS_CACHE = '';

const isColorChannelsSimilar = (r: number, g: number, b: number, color: number, gup = 0) => {
  const colorR = (color >> 16) & 0xff;
  const colorG = (color >> 8) & 0xff;
  const colorB = color & 0xff;
  return (
    r >= colorR - gup &&
    r <= colorR + gup &&
    g >= colorG - gup &&
    g <= colorG + gup &&
    b >= colorB - gup &&
    b <= colorB + gup
  );
};

const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
};

const fetchResourceAsBase64 = async (url: string): Promise<string> => {
  const response = await fetch(url);
  const arrayBuffer = await response.arrayBuffer();
  return arrayBuffer ? arrayBufferToBase64(arrayBuffer) : '';
};

const getGoogleFontFaces = async (fontsUrl: string): Promise<string> => {
  if (GOOGLE_FONTS_CACHE) {
    return GOOGLE_FONTS_CACHE;
  }
  const fontUrlRegex = /url\((https:\/\/fonts\.gstatic\.com\/[^)]+)\)/g;
  const response = await fetch(fontsUrl);
  let cssText = await response.text();

  let match;
  while ((match = fontUrlRegex.exec(cssText)) !== null) {
    const fontUrl = match[1];
    const base64Font = await fetchResourceAsBase64(fontUrl);
    if (base64Font) {
      const fontFormat = fontUrl.includes('woff2') ? 'woff2' : 'woff';
      cssText = cssText.replace(fontUrl, `data:font/${fontFormat};base64,${base64Font}`);
    }
  }
  GOOGLE_FONTS_CACHE = cssText;
  return GOOGLE_FONTS_CACHE;
};

const getCSSStyles = (removeMediaRules = false) => {
  let css = '';

  const mediasMatched = [
    window.matchMedia('(min-width: 600px)').matches ? 600 : 0,
    window.matchMedia('(min-width: 900px)').matches ? 900 : 0,
    window.matchMedia('(min-width: 1200px)').matches ? 1200 : 0,
  ].filter(Boolean);

  const mediasRegex = mediasMatched.length ? new RegExp(`min-width:\\s*(${mediasMatched.join('|')})px`) : null;

  const { styleSheets } = document;
  for (let i = 0; i < styleSheets.length; i++) {
    try {
      const rules = styleSheets[i].cssRules || styleSheets[i].rules;
      for (let j = 0; j < rules.length; j++) {
        const rule = rules[j];
        if (removeMediaRules && rule.type === CSSRule.MEDIA_RULE && mediasRegex) {
          const mediaText = (rule as CSSMediaRule).media.mediaText || '';
          if (mediaText && mediasRegex.test(mediaText)) {
            css += rule.cssText;
          }
        } else {
          css += rule.cssText;
        }
      }
    } catch (e) {
      // ...
    }
  }
  return css;
};

const loadImageAsBase64 = async (options: {
  src: string;
  padding?: number;
  format?: 'png' | 'jpeg';
  backgroundColor?: string;
  pixelRatio?: number;
  cropByColorMarker?: boolean;
}): Promise<string> => {
  const { src, padding = 0, format = 'png', backgroundColor, pixelRatio = 1, cropByColorMarker } = options;

  return new Promise((resolve) => {
    const onError = () => {
      resolve(EMPTY_IMAGE_PLACEHOLDER);
    };

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = src;
    img.onerror = onError;
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(img.width * pixelRatio);
      canvas.height = Math.round(img.height * pixelRatio);
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        return onError();
      }

      if (backgroundColor) {
        ctx.fillStyle = backgroundColor;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }

      ctx.scale(pixelRatio, pixelRatio);
      ctx.drawImage(img, 0, 0);

      let croppedCanvas;
      if (cropByColorMarker) {
        let cropMarkerFound = false;
        let cropMarkerY = 0;
        const imageData = ctx.getImageData(0, 0, 1, canvas.height).data;

        for (let y = 0; y < canvas.height; y++) {
          const index = (canvas.height - 1 - y) * 4;
          const r = imageData[index];
          const g = imageData[index + 1];
          const b = imageData[index + 2];

          if (cropMarkerFound) {
            if (!isColorChannelsSimilar(r, g, b, CROP_MARKER_COLOR, 5)) {
              cropMarkerY = y;
              break;
            }
          } else if (isColorChannelsSimilar(r, g, b, CROP_MARKER_COLOR, 5)) {
            cropMarkerFound = true;
          }
        }

        if (cropMarkerY) {
          croppedCanvas = document.createElement('canvas');
          croppedCanvas.width = canvas.width;
          croppedCanvas.height = canvas.height - cropMarkerY;
          const croppedCtx = croppedCanvas.getContext('2d');
          if (!croppedCtx) {
            return onError();
          }
          croppedCtx.drawImage(canvas, 0, 0);
        } else {
          croppedCanvas = canvas;
        }
      } else {
        croppedCanvas = canvas;
      }

      if (padding) {
        const paddingCanvas = document.createElement('canvas');
        paddingCanvas.width = croppedCanvas.width + padding * 2;
        paddingCanvas.height = croppedCanvas.height + padding * 2;
        const paddingCtx = paddingCanvas.getContext('2d');

        if (!paddingCtx) {
          return onError();
        }

        if (backgroundColor) {
          paddingCtx.fillStyle = backgroundColor;
          paddingCtx.fillRect(0, 0, paddingCanvas.width, paddingCanvas.height);
        }

        paddingCtx.drawImage(croppedCanvas, padding, padding);
        resolve(paddingCanvas.toDataURL(`image/${format}`));
      } else {
        resolve(croppedCanvas.toDataURL(`image/${format}`));
      }
    };
  });
};

const convertHTMLImagesToBase64 = async (svgElement: SVGElement) => {
  const images = svgElement.querySelectorAll('img');
  return Promise.allSettled(
    Array.from(images).map(async (img) => {
      const src = img.getAttribute('src');

      if (src && !src.startsWith('data:')) {
        const base64Image = await loadImageAsBase64({ src });
        img.setAttribute('src', base64Image);
      }
    }),
  );
};

type ElementToImageOptions = {
  /** Use the widest `clientWidth` along the DOM chain so exports stay full-width when the node sits inside a max-width column. */
  exportUseWidestAncestorWidth?: boolean;
};

export const elementToImage = async (
  element: Element,
  padding = 0,
  areGoogleFontsExcluded?: boolean,
  adaptHeight?: boolean,
  options?: ElementToImageOptions,
): Promise<string | null> => {
  if (!element) {
    return null;
  }

  const container = document.createElement('div');
  const width = getExportWidthPx(element, options?.exportUseWidestAncestorWidth);
  const height = element.clientHeight * (adaptHeight ? 1.3 : 1);
  container.style.position = 'absolute';
  container.style.top = '0';
  container.style.left = '0';

  container.innerHTML = `
  <svg xmlns="http://www.w3.org/2000/svg"
    width="${width}"
    height="${height}"
    viewBox="0 0 ${width} ${height}"
  >
    <style>
      foreignObject * {
        font-family: 'Open Sans', sans-serif;
        font-weight: 600;
      }
    </style>
    <foreignObject width="100%" height="100%">
      ${element.outerHTML}
      ${
        adaptHeight
          ? `<div style="width:10px;height:5px;background:#${CROP_MARKER_COLOR.toString(16).padStart(6, '0')}"></div>`
          : ''
      }
    </foreignObject>
  </svg>`;

  if (!container.firstElementChild) {
    return null;
  }

  const svgElement = container.firstElementChild as SVGElement;

  await convertHTMLImagesToBase64(svgElement);

  const googleFontFaces = areGoogleFontsExcluded
    ? ''
    : await getGoogleFontFaces('https://fonts.googleapis.com/css2?family=Open+Sans:wght@600;700&display=swap');

  const style = getCSSStyles(true);
  const styleElement = document.createElement('style');
  styleElement.setAttribute('type', 'text/css');
  styleElement.innerHTML = `${googleFontFaces}\n\n${style}`;
  svgElement.insertBefore(styleElement, svgElement.firstChild);

  const svgString = new XMLSerializer().serializeToString(svgElement);

  return loadImageAsBase64({
    src: BASE_64_SVG_IMAGE_PREFIX + btoa(unescape(encodeURIComponent(svgString))),
    padding,
    backgroundColor: '#fff',
    pixelRatio: window.devicePixelRatio || 1,
    cropByColorMarker: adaptHeight,
  });
};

export const htmlToImage = async (selector: string, padding?: number): Promise<string | null> => {
  const content = document.querySelector(selector);

  if (!content) {
    return null;
  }

  return elementToImage(content, padding);
};
